import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Input,
  Button,
  FormControl,
  FormLabel,
  useToast,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';

import { useStore } from '../../hooks/useStore';
import StatsCard from '../../components/cards/StatsCard';
import StatsUsersTable from '../../components/table/StatsUsersTable';
import { Colors } from '../../theme/colors';
import { toUtc } from '../../utils/time';
import ConfirmationModal from '../../components/modals/ConfirmationModal';

const DashboardHomeScreen = () => {
  const {
    statisticsStore: { fetchStatistics, statistics, loading },
    creditStore: {
      fetchInitialCredit,
      fetchReferralCredit,
      updateInitialCredit,
      updateReferralCredit,
      initialCreditValue,
      referralCreditValue,
      loading: creditLoading,
      loadingReferral,
      enableBoosters,
      updateEnableBoosters,
      fetchEnableBoosters,
    },
    listingStore: {
      fetchFlashSaleWindow,
      updateFlashSaleWindow,
      fetchEnableBinPosting,
      fetchEnablePurchasing,
      updateEnablePosting,
      updateEnablePurchasing,
      flashSaleStartTime,
      flashSaleEndTime,
      enablePosting,
      enablePurchasing,
      loading: flashLoading,
      loadingPurchasingStatus,
      loadingPostingStatus,
    },
  } = useStore();

  const cardBackground = useColorModeValue(
    Colors.light.gray3,
    Colors.dark.gray3,
  );
  const textColor = useColorModeValue(Colors.light.text, Colors.dark.text);

  const [inputValue, setInputValue] = useState('');
  const [referralValue, setReferralValue] = useState('');
  const [startTime, setStartTime] = useState('21:00');
  const [endTime, setEndTime] = useState('21:30');
  const toast = useToast();
  const {
    isOpen: isBinPostingModalOpen,
    onOpen: onBinPostingModalOpen,
    onClose: onBinPostingModalClose,
  } = useDisclosure();
  const {
    isOpen: isPurchasingModalOpen,
    onOpen: onPurchasingModalOpen,
    onClose: onPurchasingModalClose,
  } = useDisclosure();

  useEffect(() => {
    (async () => {
      await fetchStatistics();
      await fetchInitialCredit();
      await fetchReferralCredit();
      await fetchFlashSaleWindow();
      await fetchEnableBoosters();
      await fetchEnableBinPosting();
      await fetchEnablePurchasing();
    })();
  }, []);

  useEffect(() => {
    if (flashSaleStartTime) setStartTime(flashSaleStartTime);
    if (flashSaleEndTime) setEndTime(flashSaleEndTime);
  }, [flashSaleStartTime, flashSaleEndTime]);

  useEffect(() => {
    setInputValue(initialCreditValue.toString());
  }, [initialCreditValue]);

  useEffect(() => {
    setReferralValue(referralCreditValue.toString());
  }, [referralCreditValue]);

  const handleToggleBoosters = async () => {
    try {
      await updateEnableBoosters(!enableBoosters);
      toast({
        title: `Boosters ${!enableBoosters ? 'enabled' : 'disabled'}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update boosters setting',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateCredit = async () => {
    const value = parseFloat(inputValue);
    if (isNaN(value) || value < 0) {
      toast({
        title: 'Invalid value',
        description: 'Please enter a non-negative number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    await updateInitialCredit(value);
    toast({
      title: 'Initial credit value updated',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleUpdateReferralCredit = async () => {
    const value = parseFloat(referralValue);
    if (isNaN(value) || value < 0) {
      toast({
        title: 'Invalid value',
        description: 'Please enter a non-negative number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    await updateReferralCredit(value);
    toast({
      title: 'Referral credit value updated',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleUpdateWindow = async () => {
    const utcStart = toUtc(startTime);
    const utcEnd = toUtc(endTime);

    try {
      await updateFlashSaleWindow(utcStart, utcEnd);
      toast({
        title: 'Flash sale window updated',
        description: `Start (UTC): ${utcStart}, End (UTC): ${utcEnd}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update flash sale window',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleToggleBinPosting = async () => {
    try {
      await updateEnablePosting(!enablePosting);
      toast({
        title: `Posting ${!enablePosting ? 'enabled' : 'disabled'}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update BIN posting setting',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleTogglePurchasing = async () => {
    try {
      await updateEnablePurchasing(!enablePurchasing);
      toast({
        title: `Purchasing ${!enablePurchasing ? 'enabled' : 'disabled'}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update purchasing setting',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <Heading size="lg" mb={4}>
        Dashboard
      </Heading>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="200px"
        >
          <Spinner size="xl" />
        </Box>
      ) : (
        <>
          <Box display="flex" gap={8} mb={4}>
            <StatsCard
              title="Total Registered Users"
              number={statistics.numberOfUsers}
              subtitle={`${statistics.newSignUpsPast24Hours.length} new sign ups in the past 24 hours`}
            />
            <StatsCard
              title="Total Unique Bidders"
              number={statistics.numberOfUniqueBidders}
              subtitle={`${statistics.newBiddersPast24Hours.length} new bidders in the past 24 hours`}
            />
          </Box>
          <Box display="flex" gap={20}>
            {statistics.newSignUpsPast24Hours.length ? (
              <StatsUsersTable
                tableData={statistics.newSignUpsPast24Hours}
                title="New Sign Ups Past 24 Hours"
              />
            ) : null}
            {statistics.newBiddersPast24Hours.length ? (
              <StatsUsersTable
                tableData={statistics.newBiddersPast24Hours}
                title="New Bidders Past 24 Hours"
              />
            ) : null}
          </Box>
        </>
      )}
      <Box display="flex" flexDir="row" gap={7}>
        <Box
          mb={4}
          p="25px"
          borderWidth="1px"
          borderRadius="md"
          boxShadow="sm"
          width="fit-content"
          bg={cardBackground}
          color={textColor}
        >
          <FormControl>
            <FormLabel fontSize="xl" fontWeight="bold">
              Initial User Credit Value
            </FormLabel>
            <Input
              value={inputValue}
              type="number"
              onChange={(e) => setInputValue(e.target.value)}
              isDisabled={creditLoading}
            />
          </FormControl>
          <Button
            w="100%"
            py="25px"
            mt={4}
            color="green.400"
            variant="contained"
            border="2px"
            borderColor="green.400"
            colorScheme="green"
            onClick={handleUpdateCredit}
            isLoading={creditLoading}
          >
            Update Credit
          </Button>
        </Box>
        <Box
          mb={4}
          p="25px"
          borderWidth="1px"
          borderRadius="md"
          boxShadow="sm"
          width="fit-content"
          bg={cardBackground}
          color={textColor}
        >
          <FormControl>
            <FormLabel fontSize="xl" fontWeight="bold">
              Referral Credit Value
            </FormLabel>
            <Input
              value={referralValue}
              type="number"
              onChange={(e) => setReferralValue(e.target.value)}
              isDisabled={loadingReferral}
            />
          </FormControl>
          <Button
            w="100%"
            py="25px"
            mt={4}
            color="green.400"
            variant="contained"
            border="2px"
            borderColor="green.400"
            colorScheme="green"
            onClick={handleUpdateReferralCredit}
            isLoading={loadingReferral}
          >
            Update Referral Credit
          </Button>
        </Box>
      </Box>
      <Box
        mb={4}
        p="25px"
        borderWidth="1px"
        borderRadius="md"
        boxShadow="sm"
        width="fit-content"
        bg={cardBackground}
        color={textColor}
      >
        <FormControl display="flex" alignItems="center">
          <FormLabel fontSize="xl" fontWeight="bold" mb={0}>
            Enable Boosters
          </FormLabel>
          <Button
            ml={4}
            colorScheme={enableBoosters ? 'green' : 'red'}
            variant="solid"
            onClick={handleToggleBoosters}
            isLoading={creditLoading}
          >
            {enableBoosters ? 'Enabled' : 'Disabled'}
          </Button>
        </FormControl>
      </Box>
      <Box
        mb={4}
        p="25px"
        borderWidth="1px"
        borderRadius="md"
        boxShadow="sm"
        width="fit-content"
        bg={cardBackground}
        color={textColor}
      >
        <FormControl>
          <FormLabel fontSize="xl" fontWeight="bold">
            Flash Sale Start Time (Local)
          </FormLabel>
          <Input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </FormControl>
        <FormControl mt={4}>
          <FormLabel fontSize="xl" fontWeight="bold">
            Flash Sale End Time (Local)
          </FormLabel>
          <Input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </FormControl>
        <Button
          w="100%"
          py="25px"
          mt={4}
          colorScheme="blue"
          onClick={handleUpdateWindow}
          isLoading={flashLoading}
        >
          Update Flash Sale Window
        </Button>
      </Box>
      <Box
        mb={4}
        p="25px"
        borderWidth="1px"
        borderRadius="md"
        boxShadow="sm"
        width="fit-content"
        bg={cardBackground}
        color={textColor}
      >
        <FormControl display="flex" alignItems="center">
          <FormLabel fontSize="xl" fontWeight="bold" mb={0}>
            Enable Posting
          </FormLabel>
          <Button
            ml={4}
            colorScheme={enablePosting ? 'green' : 'red'}
            variant="solid"
            onClick={onBinPostingModalOpen}
            isLoading={loadingPostingStatus}
          >
            {enablePosting ? 'Enabled' : 'Disabled'}
          </Button>
        </FormControl>
      </Box>
      <Box
        mb={4}
        p="25px"
        borderWidth="1px"
        borderRadius="md"
        boxShadow="sm"
        width="fit-content"
        bg={cardBackground}
        color={textColor}
      >
        <FormControl display="flex" alignItems="center">
          <FormLabel fontSize="xl" fontWeight="bold" mb={0}>
            Enable Purchasing
          </FormLabel>
          <Button
            ml={4}
            colorScheme={enablePurchasing ? 'green' : 'red'}
            variant="solid"
            onClick={onPurchasingModalOpen}
            isLoading={loadingPurchasingStatus}
          >
            {enablePurchasing ? 'Enabled' : 'Disabled'}
          </Button>
        </FormControl>
      </Box>

      <ConfirmationModal
        isOpen={isBinPostingModalOpen}
        onClose={onBinPostingModalClose}
        title={`${enablePosting ? 'Disable' : 'Enable'} Posting`}
        subtitle={`Are you sure you want to ${enablePosting ? 'disable' : 'enable'} posting? This will affect users' ability to post listings.`}
        cancelActionLabel="Cancel"
        confirmActionLabel="Confirm"
        onCancel={() => {}}
        onConfirm={handleToggleBinPosting}
      />

      <ConfirmationModal
        isOpen={isPurchasingModalOpen}
        onClose={onPurchasingModalClose}
        title={`${enablePurchasing ? 'Disable' : 'Enable'} Purchasing`}
        subtitle={`Are you sure you want to ${enablePurchasing ? 'disable' : 'enable'} purchasing? This will affect users' ability to make purchases.`}
        cancelActionLabel="Cancel"
        confirmActionLabel="Confirm"
        onCancel={() => {}}
        onConfirm={handleTogglePurchasing}
      />
    </Box>
  );
};

export default observer(DashboardHomeScreen);
